import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, UtensilsCrossed } from 'lucide-react';
import clsx from 'clsx';
import {
  getTenantCuisines,
  listAppCuisines,
  mediaUrl,
  setTenantCuisines,
  type AppCuisine,
} from '@/lib/operatorApi';

// Карточка «Кухни в приложении» на странице арендатора: какие кухни из
// справочника (/app/cuisines) отмечены у заведения. По ним гость фильтрует
// ленту на главной People и видит теги на карточке заведения. Максимум 10.

const MAX = 10;

interface Props {
  tenantId: string;
  onSaved?: (msg: string) => void;
}

export default function TenantCuisinesCard({ tenantId, onSaved }: Props) {
  const [catalog, setCatalog] = useState<AppCuisine[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listAppCuisines(), getTenantCuisines(tenantId)])
      .then(([c, t]) => {
        if (cancelled) return;
        setCatalog(c.cuisines);
        setSaved(t.cuisineIds);
        setSelected(t.cuisineIds);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const dirty = useMemo(() => {
    if (saved.length !== selected.length) return true;
    const s = new Set(saved);
    return selected.some((id) => !s.has(id));
  }, [saved, selected]);

  const toggle = (id: string) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= MAX) return cur;
      return [...cur, id];
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await setTenantCuisines(tenantId, selected);
      setSaved(res.cuisineIds);
      setSelected(res.cuisineIds);
      onSaved?.('Кухни заведения сохранены');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  // Скрытые в справочнике кухни показываем только если уже отмечены —
  // чтобы оператор видел, почему тег не отображается в приложении.
  const visible = catalog.filter((c) => c.isActive || selected.includes(c.id));

  return (
    <div className="px-4 pb-6 md:px-8">
    <section className="card p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-medium">Кухни в приложении People</h2>
          <p className="mt-1 text-[12px] text-ink/50">
            Гость фильтрует по ним ленту на главной. Выбрано {selected.length} из {MAX}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/cuisines" className="btn-ghost text-[12px]">
            Справочник кухонь
          </Link>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void save()}
            disabled={!dirty || saving || loading}
          >
            <Check size={14} strokeWidth={1.75} />
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>

      {error && <p className="mt-3 text-[13px] text-red-600">{error}</p>}

      {loading ? (
        <div className="mt-4 text-[13px] text-ink/50">Загрузка…</div>
      ) : visible.length === 0 ? (
        <div className="mt-4 text-[13px] text-ink/50">
          Справочник пуст —{' '}
          <Link to="/app/cuisines" className="link">
            добавьте кухни
          </Link>
          , чтобы отмечать их у заведений.
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {visible.map((c) => {
            const on = selected.includes(c.id);
            const full = !on && selected.length >= MAX;
            const src = mediaUrl(c.imageUrl);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                disabled={saving || full}
                aria-pressed={on}
                className={clsx(
                  'flex items-center gap-2 border px-3 py-1.5 text-[13px] transition',
                  on
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-white text-ink/80 hover:border-ink',
                  full && 'opacity-40',
                  !c.isActive && 'border-dashed',
                )}
                title={!c.isActive ? 'Кухня скрыта в справочнике — тег не показывается' : undefined}
              >
                <span className="grid h-6 w-6 place-items-center overflow-hidden">
                  {src ? (
                    <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
                  ) : (
                    <UtensilsCrossed size={13} strokeWidth={1.5} className={on ? 'text-white/70' : 'text-ink/40'} />
                  )}
                </span>
                {c.name}
                {on && <Check size={13} strokeWidth={2} />}
              </button>
            );
          })}
        </div>
      )}
    </section>
    </div>
  );
}
